import { DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin } from 'rxjs';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { ClientQuotaDialogComponent } from '../../../components/dialogs/client-quota-dialog/client-quota-dialog.component';
import { ExtendTrialDialogComponent } from '../../../components/dialogs/extend-trial-dialog/extend-trial-dialog.component';
import { OrganisationDialogComponent } from '../../../components/dialogs/organisation-dialog/organisation-dialog.component';
import { TokenUsageDialogComponent } from '../../../components/dialogs/token-usage-dialog/token-usage-dialog.component';
import {
  AlTableApi,
  AlTableColumn,
} from '../../../components/features/table/table.component';
import { AlTableTextComponent } from '../../../components/features/table-text/table-text.component';
import { formatNumber } from '../../../helpers/formatNumbers';
import { AuthService, UserRoles } from '../../../services/auth.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ProductService } from '../../../services/product.service';

@Component({
  selector: 'app-organisations',
  templateUrl: './organisations.component.html',
  styleUrl: './organisations.component.scss',
})
export class OrganisationsComponent {
  title = '';
  loading = false;
  /** A delete is running. It removes files, agent data and the subscription
   *  on the server and takes many seconds — the table gives way to the
   *  loader for that time, so the same organisation cannot be sent twice. */
  deleting = false;
  deletingCount = 0;

  @ViewChild('table') table!: AlTableApi;

  columns: AlTableColumn[] = [
    {
      key: 'company_name', kind: 'text',
      title: 'Name',
      map: (v: string, row: any) => {
        return `${v ?? ''} (${row.email})`;
      },
    },
    {
      key: 'created_at', kind: 'date',
      title: 'Erstellt am',
      map: (i: string) => new DatePipe('de-DE', 'CET').transform(i, 'short'),
    },
    {
      // Last portal login by ANY user of the organisation (access_log
      // 'last_login' max per client). Super-admin impersonation writes no
      // access_log row, so it never counts as activity.
      key: 'last_login_at', kind: 'date',
      title: 'Letzter Login',
      map: (i: string | null) =>
        i ? new DatePipe('de-DE', 'CET').transform(i, 'short') : '—',
    },
    {
      // Planname vom Backend (plan_display_name / plan_name). Vertragsplaene
      // (Migration 070) stehen nicht in der festen Liste im ProductService und
      // erschienen vorher als „Not found“; die Liste bleibt nur Rueckfall.
      key: 'current_plan',
      title: 'Plan',
      component: AlTableTextComponent,
      componentInputs: ({ row }) => ({
        text: this.planLabel(row),
        max: 80,
        suffix: row?.is_contract
          ? this.t.instant('pages.admin.organisations.contractBadge')
          : undefined,
        suffixTooltip: row?.is_contract
          ? this.t.instant('pages.admin.organisations.contractBadgeTooltip')
          : undefined,
      }),
    },
    {
      key: 'subscription_status',
      title: 'Abo Status',
    },
    {
      key: 'is_confirmed',
      title: 'Bestätigt',
    },
    {
      key: 'token_usage_count', kind: 'number',
      title: 'Token Nutzung',
      sortDefault: 'asc',
      map: (i: number, row: any) =>
        `${formatNumber(
          row.token_usage_count || 0
        )} (${row.token_usage_percentage?.toFixed(1)}%)`,
    },
    {
      key: 'trial_used',
      title: 'Trial',
      map: (i: number, row: any) => (i ? 'Ja' : 'Nein'),
    },

    {
      key: 'features', kind: 'text',
      title: 'Features',
      map: (f) => {
        return f.join(', ');
      },
    },
  ];

  rowActions: any[] = [
    {
      key: 'edit',
      title: this.t.instant('buttons.edit'),
      icon: 'edit-outline',
    },
    {
      key: 'impersonate',
      title: 'Impersonate',
      icon: 'people-outline',
    },
    {
      key: 'extendTrial',
      title: 'Extend Trial',
      icon: 'gift-outline',
    },
    {
      key: 'tokenUsage',
      title: 'Token Usage',
      icon: 'activity-outline',
    },
    {
      key: 'quotaLimit',
      title: 'Credits & Quota',
      icon: 'shield-outline',
    },
    {
      // Escape hatch for a registration whose welcome mail never arrived: the
      // client row is already committed, so the address is taken and the user
      // can neither re-register nor reach the confirm page that carries the
      // self-service resend button.
      key: 'resendConfirmation',
      title: this.t.instant('buttons.resendConfirmation'),
      icon: 'email-outline',
      disabled: (row: any) => !this.isSuperAdmin || !!row?.is_confirmed,
      tooltip: (row: any) => {
        const g = 'pages.admin.organisations.';
        if (!this.isSuperAdmin) {
          return this.t.instant(g + 'resendTooltipSuperAdminOnly');
        }
        if (row?.is_confirmed) {
          return this.t.instant(g + 'resendTooltipConfirmed');
        }
        return this.t.instant(g + 'resendTooltip', { email: row?.email ?? '' });
      },
    },
    {
      key: 'delete',
      title: this.t.instant('buttons.delete'),
      icon: 'trash-outline',
    },
  ];

  globalActions = [];

  // Golden "Demo Master" client (migration 098). Super-admins impersonate
  // it to configure the demo agents that get cloned into new tenants.
  private readonly DEMO_MASTER_CLIENT_ID = 'd3300000-0000-0000-0000-000000000001';

  data: any[] = [];

  /** Current search text (name, e-mail, plan, subscription status, features). */
  search = '';

  /** `data` after applying `search`. Kept as a field, not a getter — a getter
   *  returning a fresh array on every change-detection run makes `app-table`
   *  rebuild its subtree and swallow clicks (CLAUDE.md rule 18). */
  filteredData: any[] = [];

  constructor(
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService,
    protected t: TranslateService,
    protected organisationService: OrganisationService,
    protected productService: ProductService,
    protected authService: AuthService
  ) {}

  ngOnInit() {
    this.loading = true;
    this.getOrganisations();

    this.organisationService.organisations$.subscribe((data) => {
      this.data = [...data];
      this.applyFilter();
    });
  }

  onSearchChange(value: string) {
    this.search = value ?? '';
    this.applyFilter();
  }

  /** Recomputes `filteredData` from `data` + `search`. Matches against the
   *  same values the columns render, so what you see is what you can find. */
  private applyFilter() {
    const q = this.search.trim().toLowerCase();
    if (!q) {
      this.filteredData = this.data;
      return;
    }
    this.filteredData = this.data.filter((row) =>
      [
        row.company_name,
        row.email,
        this.planLabel(row),
        row.is_contract
          ? this.t.instant('pages.admin.organisations.contractBadge')
          : null,
        row.subscription_status,
        ...(row.features || []),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }

  getOrganisations() {
    this.organisationService.listAllOrganisations().subscribe(() => {
      this.loading = false;
    });
  }

  get isSuperAdmin(): boolean {
    return (
      this.authService.accessTokenPayload$.value?.role === UserRoles.SuperAdmin
    );
  }

  // Impersonate the Demo Master client, then hard-reload into its
  // dashboard so the new token's context takes effect immediately.
  impersonateMaster() {
    this.authService.impersonate(this.DEMO_MASTER_CLIENT_ID).subscribe(() => {
      window.location.href = '/';
    });
  }

  onEvent(e: { event: string; data: any }) {
    const { action, ids, rows, id, row } = e.data;

    switch (e.event) {
      case 'multiAction':
        if (action.key === 'delete') {
          this.deleteIds(ids);
          return;
        }

        break;
      case 'rowAction':
        if (action.key === 'extendTrial') {
          this.dialogService
            .open(ExtendTrialDialogComponent, {
              context: {
                data: row,
              },
            })
            .onClose.subscribe(() => {
              this.getOrganisations();
            });
          return;
        }

        if (action.key === 'impersonate') {
          this.authService.impersonate(id).subscribe();
          return;
        }
        if (action.key === 'tokenUsage') {
          this.dialogService.open(TokenUsageDialogComponent, {
            context: {
              data: row,
            },
          });
          return;
        }
        if (action.key === 'quotaLimit') {
          this.dialogService
            .open(ClientQuotaDialogComponent, {
              context: {
                data: row,
              },
            })
            .onClose.subscribe((changed) => {
              if (changed) {
                this.getOrganisations();
              }
            });
          return;
        }
        if (action.key === 'resendConfirmation') {
          this.resendConfirmation(row);
          return;
        }
        if (action.key === 'delete') {
          this.deleteIds([id]);
          return;
        }
        if (action.key === 'edit') {
          this.dialogService
            .open(OrganisationDialogComponent, {
              context: {
                data: row,
              },
            })
            .onClose.subscribe((contractChanged) => {
              // Vertrag aktiviert/geaendert/beendet: Plan-Spalte und
              // Vertragsmarke kommen vom Backend, also neu laden.
              if (contractChanged) {
                this.getOrganisations();
              }
            });
        }
        break;
      case 'globalAction':
        break;
      default:
        break;
    }
  }

  deleteIds(ids: string[]) {
    if (this.deleting) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.confirmDialog.deleteEntries.title',
          message: 'dialogs.confirmDialog.deleteEntries.title',
        },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) {
          if (this.deleting) return;
          this.deleting = true;
          this.deletingCount = ids.length;

          const deletes$ = ids.map((id) => {
            return this.organisationService.deleteOrganisation(id);
          });

          // Without an error branch a failing delete (e.g. the tutor/file
          // cleanup call returning 502) left the table spinning forever with
          // no message — the deletion looked like it silently did nothing.
          forkJoin(deletes$)
            .pipe(finalize(() => (this.deleting = false)))
            .subscribe({
            next: () => {
              this.nbToastrService.success(
                'Erfolgreich',
                `${ids.length} Organisationen gelöscht`
              );
              this.organisationService.listAllOrganisations().subscribe();
            },
            error: (err) => {
              this.nbToastrService.danger(
                this.t.instant('toasts.error'),
                err?.error?.detail ||
                  this.t.instant('pages.admin.organisations.deleteFailed')
              );
              console.error('[organisations] delete failed', err);
              this.organisationService.listAllOrganisations().subscribe();
            },
          });
        }
      });
  }

  // Sends a fresh 24h confirmation token to the organisation's admin address.
  // Reuses the same public endpoint the confirm-registration page calls, so
  // there is exactly one token-issuing path to reason about.
  resendConfirmation(row: any) {
    const g = 'pages.admin.organisations.';
    const email = row?.email;
    if (!email) {
      this.nbToastrService.danger(
        this.t.instant('toasts.error'),
        this.t.instant(g + 'resendNoEmail')
      );
      return;
    }

    this.authService.resendConfirmation(email).subscribe({
      next: () => {
        this.nbToastrService.success(
          this.t.instant('toasts.success'),
          this.t.instant(g + 'resendSuccess', { email })
        );
      },
      error: (err) => {
        this.nbToastrService.danger(
          this.t.instant('toasts.error'),
          this.t.instant(g + 'resendFailed', { email })
        );
        console.error('[organisations] resend confirmation failed', err);
      },
    });
  }

  /** Anzeigename des Plans: Vertragsname > uebersetzter Planname aus dem
   *  Backend > feste Produktliste (Rueckfall fuer aeltere Antworten). */
  planLabel(row: any): string {
    if (row?.plan_display_name) {
      return row.plan_display_name;
    }
    const known = this.productService.getPlanById(row?.current_plan);
    if (known) {
      return this.t.instant(known.name);
    }
    if (row?.plan_name) {
      return row.plan_name;
    }
    return this.t.instant(this.getPlanName(row?.current_plan));
  }

  getPlanName(plandId?: string) {
    const plan = this.productService.products$.value.find(
      (p) => p.id === plandId
    );

    if (plan) {
      return plan.name;
    }
    return 'Not found';
  }
}
